import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FolderOpen, Search, Trash2, Copy, Edit2, X, Loader2, Download,
  Image as ImageIcon, FileText, Video, Film,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import FileUploader, { FileKindIcon, formatBytes } from '../../components/media/FileUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const KIND_LABELS = [
  { k: 'all',      label: 'All',       icon: FolderOpen },
  { k: 'image',    label: 'Images',    icon: ImageIcon },
  { k: 'pdf',      label: 'PDFs',      icon: FileText },
  { k: 'document', label: 'Documents', icon: FileText },
  { k: 'video',    label: 'Videos',    icon: Video },
  { k: 'other',    label: 'Other',     icon: Film },
];

/**
 * MediaLibraryPage — DCCF Media Library.
 *
 * One place to browse / search / upload / delete every file previously
 * uploaded through the admin surfaces. Powered by Emergent Object Storage
 * and the server-side `media_files` collection; nothing lives in Mongo
 * as base64.
 */
const MediaLibraryPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [kind, setKind] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (kind !== 'all') params.kind = kind;
      if (search.trim()) params.search = search.trim();
      const [list, sum] = await Promise.all([
        axios.get(`${API}/admin/media`, { params, withCredentials: true }),
        axios.get(`${API}/admin/media/summary`, { withCredentials: true }),
      ]);
      setItems(list.data.items || []);
      setTotal(list.data.total || 0);
      setSummary(sum.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load media');
    } finally {
      setLoading(false);
    }
  }, [kind, search]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (m) => {
    if (!window.confirm(`Delete "${m.original_filename}"? This cannot be undone from the library.`)) return;
    try {
      await axios.delete(`${API}/admin/media/${m.id}`, { withCredentials: true });
      setItems((prev) => prev.filter((x) => x.id !== m.id));
      toast.success('File removed');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const copyUrl = (m) => {
    const full = `${BACKEND}${m.url}`;
    navigator.clipboard?.writeText(full).then(
      () => toast.success('URL copied'),
      () => toast.error('Could not copy'),
    );
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const { data } = await axios.patch(
        `${API}/admin/media/${editing.id}`,
        { alt_text: editing.alt_text || null, tags: editing.tags || [] },
        { withCredentials: true },
      );
      setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      toast.success('Updated');
      setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className="p-4 sm:p-6" data-testid="media-library-page">
      {/* Header */}
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-[#002249] text-white flex items-center justify-center">
          <FolderOpen className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-[#1a1a1a]">Media Library</h1>
          <p className="text-sm text-slate-500 mt-1">
            Every file uploaded through DCCF — images, PDFs, Office docs, short videos. Up to {summary?.limit_per_file_human || '50 MB'} per file.
          </p>
        </div>
      </div>

      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5">
          <StatTile label="Total files" value={summary.total_count} />
          <StatTile label="Total size" value={summary.total_human} />
          <StatTile label="Images" value={summary.by_kind?.image?.count || 0} />
          <StatTile label="Documents + PDFs" value={(summary.by_kind?.pdf?.count || 0) + (summary.by_kind?.document?.count || 0)} />
          <StatTile label="Videos" value={summary.by_kind?.video?.count || 0} />
        </div>
      )}

      {/* Uploader */}
      <div className="mb-5">
        <FileUploader onAllDone={load} />
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by filename, alt text or tag…"
            className="pl-9"
            data-testid="media-search"
          />
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {KIND_LABELS.map(({ k, label, icon: Icon }) => (
            <button
              key={k}
              onClick={() => setKind(k)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors ${
                kind === k
                  ? 'bg-[#002249] text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
              data-testid={`media-kind-${k}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="text-xs text-slate-500 mb-2">
        {loading ? 'Loading…' : `${total} file(s)`}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="media-grid">
        {items.map((m) => (
          <MediaTile
            key={m.id}
            media={m}
            onPreview={() => setPreview(m)}
            onDelete={() => handleDelete(m)}
            onCopy={() => copyUrl(m)}
            onEdit={() => setEditing({ ...m, tags: m.tags || [] })}
          />
        ))}
      </div>

      {items.length === 0 && !loading && (
        <div className="text-center text-slate-400 py-16 border border-dashed border-slate-200 rounded-2xl mt-3">
          No files yet. Drop something into the uploader above to get started.
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <PreviewModal media={preview} onClose={() => setPreview(null)} />
      )}

      {/* Edit modal */}
      {editing && (
        <EditModal
          editing={editing}
          setEditing={setEditing}
          onSave={saveEdit}
        />
      )}
    </div>
  );
};

// ─── Pieces ─────────────────────────────────────────────────────────

const StatTile = ({ label, value }) => (
  <div className="bg-white rounded-lg px-3 py-2 border border-slate-200">
    <div className="text-[10.5px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
    <div className="text-base font-bold text-[#1a1a1a] mt-0.5">
      {typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}
    </div>
  </div>
);

const MediaTile = ({ media, onPreview, onDelete, onCopy, onEdit }) => {
  const isImage = media.kind === 'image';
  const url = `${BACKEND}${media.url}`;
  return (
    <div
      className="group rounded-xl border border-slate-200 bg-white overflow-hidden hover:border-slate-300 transition-colors"
      data-testid={`media-tile-${media.id}`}
    >
      <button
        onClick={onPreview}
        className="block w-full aspect-square bg-slate-50 relative overflow-hidden"
      >
        {isImage ? (
          <img
            src={url}
            alt={media.alt_text || media.original_filename}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <FileKindIcon kind={media.kind} className="w-8 h-8" />
          </div>
        )}
        <div className="absolute top-1.5 left-1.5">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/90 text-slate-600 backdrop-blur-sm">
            {media.kind}
          </span>
        </div>
      </button>
      <div className="p-2">
        <div className="text-[12px] font-semibold text-[#1a1a1a] truncate" title={media.original_filename}>
          {media.original_filename}
        </div>
        <div className="text-[10.5px] text-slate-500 mt-0.5">
          {formatBytes(media.size_bytes)} · {new Date(media.created_at).toLocaleDateString()}
        </div>
        <div className="flex items-center gap-1 mt-1.5 opacity-70 group-hover:opacity-100 transition-opacity">
          <IconBtn onClick={onCopy} title="Copy URL" testid={`media-copy-${media.id}`}><Copy className="w-3 h-3" /></IconBtn>
          <IconBtn onClick={onEdit} title="Edit metadata" testid={`media-edit-${media.id}`}><Edit2 className="w-3 h-3" /></IconBtn>
          <a href={url} target="_blank" rel="noreferrer" className="p-1 rounded hover:bg-slate-100 text-slate-500 inline-flex" title="Open in new tab">
            <Download className="w-3 h-3" />
          </a>
          <IconBtn onClick={onDelete} title="Delete" testid={`media-delete-${media.id}`} danger><Trash2 className="w-3 h-3" /></IconBtn>
        </div>
      </div>
    </div>
  );
};

const IconBtn = ({ onClick, title, children, testid, danger }) => (
  <button
    onClick={onClick}
    title={title}
    data-testid={testid}
    className={`p-1 rounded hover:bg-slate-100 ${danger ? 'text-rose-500 hover:text-rose-700 hover:bg-rose-50' : 'text-slate-500 hover:text-[#002249]'}`}
  >
    {children}
  </button>
);

const PreviewModal = ({ media, onClose }) => {
  const url = `${BACKEND}${media.url}`;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="media-preview-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 h-12 border-b border-slate-100">
          <div className="font-semibold text-[13px] text-[#1a1a1a] truncate">{media.original_filename}</div>
          <button onClick={onClose} className="p-1 rounded-md text-slate-500 hover:bg-slate-50" data-testid="media-close-preview">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {media.kind === 'image' && (
            <img src={url} alt={media.alt_text || ''} className="w-full h-auto rounded-lg border border-slate-200 bg-slate-50" />
          )}
          {media.kind === 'video' && (
            <video src={url} controls className="w-full rounded-lg border border-slate-200 bg-black" />
          )}
          {media.kind === 'pdf' && (
            <iframe src={url} title={media.original_filename} className="w-full h-[70vh] rounded-lg border border-slate-200" />
          )}
          {['document', 'other'].includes(media.kind) && (
            <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#002249] hover:underline">
              <Download className="w-4 h-4" /> Download {media.original_filename}
            </a>
          )}
          <div className="grid grid-cols-2 gap-2 text-[12px] text-slate-600 pt-2 border-t border-slate-100">
            <KV label="Type" value={media.mime_type} />
            <KV label="Size" value={formatBytes(media.size_bytes)} />
            <KV label="Uploaded by" value={media.uploader_name || '—'} />
            <KV label="Uploaded" value={new Date(media.created_at).toLocaleString()} />
            {media.alt_text && <KV label="Alt text" value={media.alt_text} full />}
            {media.tags?.length > 0 && <KV label="Tags" value={media.tags.join(', ')} full />}
          </div>
        </div>
      </div>
    </div>
  );
};

const KV = ({ label, value, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <div className="text-[10.5px] uppercase font-semibold tracking-wide text-slate-500">{label}</div>
    <div className="text-slate-700 break-words">{value}</div>
  </div>
);

const EditModal = ({ editing, setEditing, onSave }) => (
  <div
    className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
    onClick={() => setEditing(null)}
    data-testid="media-edit-modal"
  >
    <div
      className="bg-white rounded-2xl max-w-md w-full shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-4 h-12 border-b border-slate-100">
        <div className="font-semibold text-[13px] text-[#1a1a1a]">Edit metadata</div>
        <button onClick={() => setEditing(null)} className="p-1 rounded-md text-slate-500 hover:bg-slate-50">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <label className="text-[11px] uppercase font-semibold tracking-wide text-slate-500">Alt text</label>
          <Input
            value={editing.alt_text || ''}
            onChange={(e) => setEditing({ ...editing, alt_text: e.target.value })}
            placeholder="Describe this file for accessibility and search"
            className="mt-1"
            data-testid="media-edit-alt"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase font-semibold tracking-wide text-slate-500">Tags</label>
          <Input
            value={(editing.tags || []).join(', ')}
            onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })}
            placeholder="comma, separated, tags"
            className="mt-1"
            data-testid="media-edit-tags"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
          <Button onClick={onSave} className="bg-[#002249] hover:bg-[#0a3570]" data-testid="media-edit-save">
            Save changes
          </Button>
        </div>
      </div>
    </div>
  </div>
);

export default MediaLibraryPage;
